import { Controller } from "@hotwired/stimulus"
import DropzoneModule from "dropzone"
const Dropzone = DropzoneModule.default || DropzoneModule
import { DirectUpload } from "@rails/activestorage"

const previewTemplate = `
<div class="dz-preview dz-file-preview !m-0 !p-0 !block w-full">
  <div class="flex items-center justify-between p-2 bg-gray-50 rounded w-full">
    <div class="flex items-center gap-2 min-w-0">
      <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
      <span class="text-sm truncate" data-dz-name></span>
      <span class="text-xs text-gray-400 shrink-0" data-dz-size></span>
    </div>
    <span class="shrink-0 ml-2" data-upload-status>
      <svg class="w-4 h-4 text-gray-300 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
    </span>
  </div>
</div>`

export default class extends Controller {
  static targets = ["dropzone", "preview", "status", "continueButton"]
  static values = {
    url: String,
    directUploadUrl: String,
    nextUrl: String,
    acceptedFiles: { type: String, default: ".pdf,.docx,.xlsx,.csv,.txt,.md" }
  }

  connect() {
    this.uploadedCount = 0
    this.failedCount = 0
    Dropzone.autoDiscover = false
    this.myDropzone = new Dropzone(this.dropzoneTarget, {
      url: "/", // placeholder — uploads handled via DirectUpload
      previewsContainer: this.previewTarget,
      clickable: this.dropzoneTarget,
      acceptedFiles: this.acceptedFilesValue,
      previewTemplate: previewTemplate,
      autoQueue: false,
      uploadMultiple: false,
      createImageThumbnails: false,
      dictDefaultMessage: ""
    })

    this.myDropzone.on("addedfile", (file) => {
      this.uploadFile(file)
    })
  }

  disconnect() {
    if (this.myDropzone) {
      this.myDropzone.destroy()
    }
  }

  async uploadFile(file) {
    const csrfToken = document.querySelector('[name="csrf-token"]').content
    const statusEl = file.previewElement?.querySelector("[data-upload-status]")

    try {
      const signedId = await this.directUpload(file)

      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ article: { kind: "attachment", file: signedId } })
      })

      if (response.ok) {
        this.uploadedCount++
        if (statusEl) {
          statusEl.innerHTML = `<svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>`
        }
      } else {
        this.failedCount++
        if (statusEl) {
          statusEl.innerHTML = `<svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>`
        }
      }
    } catch {
      this.failedCount++
      if (statusEl) {
        statusEl.innerHTML = `<svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>`
      }
    }

    this.updateStatus()
  }

  updateStatus() {
    this.statusTarget.classList.remove("hidden")

    if (this.failedCount > 0) {
      this.statusTarget.textContent = `${this.uploadedCount} uploaded, ${this.failedCount} failed.`
      this.statusTarget.className = "text-sm text-red-600"
    } else {
      this.statusTarget.textContent = `${this.uploadedCount} file${this.uploadedCount === 1 ? "" : "s"} uploaded.`
      this.statusTarget.className = "text-sm text-green-600"
    }

    if (this.uploadedCount > 0 && this.hasContinueButtonTarget) {
      this.continueButtonTarget.classList.remove("hidden")
    }
  }

  continue() {
    window.location.href = this.nextUrlValue
  }

  directUpload(file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue)
      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }
}
